import {
  createEditor,
  TextNode,
  $createTextNode,
  FORMAT_TEXT_COMMAND,
  $getSelection,
  $isRangeSelection,
  UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
} from "lexical";
import { HeadingNode, QuoteNode, registerRichText } from "@lexical/rich-text";
import { registerHistory, createEmptyHistoryState } from "@lexical/history";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  registerMarkdownShortcuts,
  BOLD_STAR,
  BOLD_ITALIC_STAR,
  ITALIC_STAR,
  STRIKETHROUGH,
  INLINE_CODE,
  CODE,
  LINK,
  QUOTE,
  HEADING,
} from "@lexical/markdown";
import { LinkNode, $isLinkNode, $toggleLink } from "@lexical/link";
import { CodeNode, CodeHighlightNode } from "@lexical/code";

// ── VariableNode ──────────────────────────────────────────────────────────────
// Atomic pill for {{ variable }} template tokens.

export class VariableNode extends TextNode {
  static getType() {
    return "variable";
  }

  static clone(node) {
    const cloned = new VariableNode(node.__text, node.__key);
    cloned.__mode = node.__mode;
    return cloned;
  }

  createDOM(config) {
    const dom = super.createDOM(config);
    dom.classList.add("dc-variable-token");
    return dom;
  }

  isToken() {
    return true;
  }

  getVariableValue() {
    return this.__text.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "");
  }

  static importJSON(serializedNode) {
    const node = $createVariableNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON() {
    return { ...super.exportJSON(), type: "variable", version: 1 };
  }
}

export function $createVariableNode(text) {
  return new VariableNode(text).setMode("token");
}

export function $isVariableNode(node) {
  return node instanceof VariableNode;
}

// ── SpoilerNode ───────────────────────────────────────────────────────────────
// Atomic pill for ||spoiler|| tokens. Click inside the preview to toggle reveal.

export class SpoilerNode extends TextNode {
  static getType() {
    return "spoiler";
  }

  static clone(node) {
    const cloned = new SpoilerNode(node.__text, node.__key);
    cloned.__mode = node.__mode;
    return cloned;
  }

  createDOM(config) {
    const dom = super.createDOM(config);
    dom.classList.add("dc-spoiler-token");
    dom.addEventListener("click", () => {
      dom.classList.toggle("dc-spoiler-token--revealed");
    });
    return dom;
  }

  isToken() {
    return true;
  }

  static importJSON(serializedNode) {
    const node = $createSpoilerNode(serializedNode.text);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON() {
    return { ...super.exportJSON(), type: "spoiler", version: 1 };
  }
}

export function $createSpoilerNode(text) {
  return new SpoilerNode(text).setMode("token");
}

export function $isSpoilerNode(node) {
  return node instanceof SpoilerNode;
}

// ── Transformers ──────────────────────────────────────────────────────────────

const UNDERLINE_TRANSFORMER = {
  format: ["underline"],
  tag: "__",
  type: "text-format",
};

const VARIABLE_TRANSFORMER = {
  dependencies: [VariableNode],
  export: (node) => {
    if (!$isVariableNode(node)) return null;
    return `{{ ${node.getVariableValue()} }}`;
  },
  importRegExp: /\{\{\s*([^}]+?)\s*\}\}/,
  regExp: /\{\{\s*([^}]+?)\s*\}\}/,
  replace: (textNode, match) => {
    textNode.replace($createVariableNode(match[0]));
  },
  trigger: "}",
  type: "text-match",
};

// Only http(s) URLs may become live link nodes — mirrors #safeImgUrl() in
// message_editor_controller.js, the codebase's established guard against
// javascript:/data: URLs reaching the DOM via a user-controlled href.
function isSafeUrl(url) {
  return /^https?:\/\//i.test(String(url ?? "").trim());
}

// Discord embeds use [Text](URL) — no title attribute, no HTML <a> tags.
const DISCORD_LINK_TRANSFORMER = {
  ...LINK,
  export: (node, exportChildren) => {
    if (!$isLinkNode(node)) return null;
    return `[${exportChildren(node)}](${node.getURL()})`;
  },
  // Guards the markdown-import path (e.g. $convertFromMarkdownString on load)
  // — a malicious javascript: URL could otherwise be planted directly via
  // saved/crafted markdown without ever going through applyLink().
  replace: (textNode, match) => {
    if (!isSafeUrl(match[2])) return;
    return LINK.replace(textNode, match);
  },
};

const SPOILER_TRANSFORMER = {
  dependencies: [SpoilerNode],
  export: (node) => {
    if (!$isSpoilerNode(node)) return null;
    return `||${node.getTextContent()}||`;
  },
  importRegExp: /\|\|([^|]+)\|\|/,
  regExp: /\|\|([^|]+)\|\|/,
  replace: (textNode, match) => {
    textNode.replace($createSpoilerNode(match[1]));
  },
  trigger: "|",
  type: "text-match",
};

export const DISCORD_MARKDOWN_TRANSFORMERS = [
  // Wave 1: inline formats
  BOLD_ITALIC_STAR,
  BOLD_STAR,
  ITALIC_STAR,
  STRIKETHROUGH,
  INLINE_CODE,
  UNDERLINE_TRANSFORMER,
  // Wave 2: variables + links
  VARIABLE_TRANSFORMER,
  DISCORD_LINK_TRANSFORMER,
  // Wave 3: blocks + spoilers
  CODE,
  QUOTE,
  HEADING,
  SPOILER_TRANSFORMER,
];

// ── Theme ─────────────────────────────────────────────────────────────────────

const EDITOR_THEME = {
  heading: {
    h1: "dc-lex-h1",
    h2: "dc-lex-h2",
    h3: "dc-lex-h3",
  },
  link: "dc-lex-link",
  quote: "dc-lex-quote",
  code: "dc-lex-codeblock",
  codeHighlight: {},
  text: {
    bold: "dc-lex-bold",
    italic: "dc-lex-italic",
    strikethrough: "dc-lex-strike",
    underline: "dc-lex-underline",
    code: "dc-lex-code",
  },
};

// ── Floating Toolbar ──────────────────────────────────────────────────────────

const TOOLBAR_FORMATS = [
  { key: "bold", icon: "fa-bold", label: "Bold" },
  { key: "italic", icon: "fa-italic", label: "Italic" },
  { key: "underline", icon: "fa-underline", label: "Underline" },
  { key: "strikethrough", icon: "fa-strikethrough", label: "Strikethrough" },
  { key: "code", icon: "fa-code", label: "Inline code" },
  { key: "link", icon: "fa-link", label: "Link", custom: "link" },
  { key: "spoiler", icon: "fa-eye-slash", label: "Spoiler", custom: "spoiler" },
];

function mountUrlInputRow(toolbar, container, editor, { maxLength, onAtLimit, strings = {} }) {
  const urlRow = document.createElement("div");
  urlRow.className = "flex items-center gap-1 mt-1";
  urlRow.style.display = "none";
  const urlInput = document.createElement("input");
  urlInput.type = "text";
  urlInput.className = "input input-xs flex-1";
  urlInput.placeholder = "https://";
  urlInput.setAttribute("aria-label", strings.linkUrl || "Link URL");
  const urlOk = document.createElement("button");
  urlOk.type = "button";
  urlOk.className = "btn btn-xs btn-primary";
  urlOk.setAttribute("aria-label", strings.applyLink || "Apply link");
  urlOk.innerHTML = '<i class="fa-solid fa-check"></i>';
  const urlCancel = document.createElement("button");
  urlCancel.type = "button";
  urlCancel.className = "btn btn-xs btn-ghost";
  urlCancel.setAttribute("aria-label", strings.cancel || "Cancel");
  urlCancel.innerHTML = '<i class="fa-solid fa-xmark"></i>';
  urlRow.append(urlInput, urlOk, urlCancel);
  toolbar.appendChild(urlRow);

  function applyLink(url) {
    if (!url?.trim() || !isSafeUrl(url)) return;
    if (maxLength !== null) {
      // Overhead: [](url) = 4 chars + url length; selected text is already counted.
      const currentLen = editor
        .getEditorState()
        .read(() => $convertToMarkdownString(DISCORD_MARKDOWN_TRANSFORMERS).length);
      if (currentLen + 4 + url.trim().length > maxLength) {
        onAtLimit?.();
        return;
      }
    }
    editor.update(() => $toggleLink(url.trim()));
  }

  urlOk.addEventListener("mousedown", (e) => {
    e.preventDefault();
    const url = urlInput.value;
    urlRow.style.display = "none";
    applyLink(url);
    container.focus();
  });

  urlCancel.addEventListener("mousedown", (e) => {
    e.preventDefault();
    urlRow.style.display = "none";
    container.focus();
  });

  urlInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const url = urlInput.value;
      urlRow.style.display = "none";
      applyLink(url);
      container.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      urlRow.style.display = "none";
      container.focus();
    }
  });

  return {
    show() {
      urlRow.style.display = "flex";
      urlInput.value = "";
      requestAnimationFrame(() => urlInput.focus());
    },
    hide() {
      urlRow.style.display = "none";
    },
  };
}

function mountFloatingToolbar(
  container,
  editor,
  { maxLength = null, onAtLimit = null, strings = {} } = {},
) {
  const toolbar = document.createElement("div");
  toolbar.className =
    "dc-float-toolbar flex flex-col items-stretch p-1 bg-base-200 border border-base-300 rounded-lg shadow-lg";
  toolbar.style.display = "none";
  document.body.appendChild(toolbar);

  const btnRow = document.createElement("div");
  btnRow.className = "flex items-center gap-0.5";
  toolbar.appendChild(btnRow);

  const urlHandlers = mountUrlInputRow(toolbar, container, editor, {
    maxLength,
    onAtLimit,
    strings,
  });

  const btns = {};
  TOOLBAR_FORMATS.forEach(({ key, icon, label, custom }) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn btn-ghost btn-xs";
    btn.setAttribute("aria-label", strings[key] || label);
    btn.innerHTML = `<i class="fa-solid ${icon}"></i>`;
    btn.addEventListener("mousedown", (e) => {
      e.preventDefault();
      if (custom === "link") {
        const isLinked = editor.getEditorState().read(() => {
          const sel = $getSelection();
          if (!$isRangeSelection(sel) || sel.isCollapsed()) return false;
          return sel.getNodes().some((n) => $isLinkNode(n) || $isLinkNode(n.getParent?.()));
        });
        if (isLinked) {
          editor.update(() => $toggleLink(null));
        } else {
          urlHandlers.show();
        }
      } else if (custom === "spoiler") {
        editor.update(() => {
          const sel = $getSelection();
          if (!$isRangeSelection(sel) || sel.isCollapsed()) return;
          const spoilers = sel.getNodes().filter($isSpoilerNode);
          if (spoilers.length > 0) {
            spoilers.forEach((node) => node.replace($createTextNode(node.getTextContent())));
          } else {
            const text = sel.getTextContent();
            const spoilerNode = $createSpoilerNode(text);
            sel.insertNodes([spoilerNode]);
            spoilerNode.select(0, text.length);
          }
        });
      } else {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, key);
      }
    });
    btnRow.appendChild(btn);
    btns[key] = btn;
  });

  const reposition = () => {
    const rect = container.getBoundingClientRect();
    toolbar.style.left = `${rect.left + rect.width / 2}px`;
    toolbar.style.top = `${rect.top}px`;
  };

  container.addEventListener(
    "focus",
    () => {
      reposition();
      toolbar.style.display = "flex";
    },
    { capture: true },
  );

  container.addEventListener(
    "blur",
    (e) => {
      if (toolbar.contains(e.relatedTarget)) return;
      toolbar.style.display = "none";
      urlHandlers.hide();
    },
    { capture: true },
  );

  editor.registerUpdateListener(({ editorState }) => {
    reposition();
    editorState.read(() => {
      const sel = $getSelection();
      const hasRange = $isRangeSelection(sel) && !sel.isCollapsed();
      TOOLBAR_FORMATS.forEach(({ key, custom }) => {
        if (custom === "link") {
          const inLink =
            hasRange && sel.getNodes().some((n) => $isLinkNode(n) || $isLinkNode(n.getParent?.()));
          btns[key].classList.toggle("btn-active", inLink);
        } else if (custom === "spoiler") {
          const inSpoiler = hasRange && sel.getNodes().some($isSpoilerNode);
          btns[key].classList.toggle("btn-active", inSpoiler);
        } else {
          btns[key].classList.toggle("btn-active", hasRange && sel.hasFormat(key));
        }
      });
    });
  });

  editor.registerRootListener((root) => {
    if (root === null) toolbar.remove();
  });
}

// ── Factory ───────────────────────────────────────────────────────────────────

// Markdown syntax overhead added per format type when toggling on.
const FORMAT_MD_OVERHEAD = { bold: 4, italic: 2, underline: 4, strikethrough: 4, code: 2 };

export function createDescriptionEditor(
  container,
  initialMarkdown,
  onChange,
  { maxLength = null, onAtLimit = null, strings = {} } = {},
) {
  const editor = createEditor({
    namespace: "DiscordDescription",
    nodes: [
      HeadingNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      LinkNode,
      VariableNode,
      SpoilerNode,
    ],
    onError: (err) => console.error("[DiscordDesc]", err),
    theme: EDITOR_THEME,
  });

  // ── maxLength enforcement ─────────────────────────────────────────────────
  // All handlers are registered BEFORE setRootElement so they are first in the
  // capture queue and stopImmediatePropagation() reliably beats Lexical's own
  // handlers.
  let _undoPending = false;

  if (maxLength !== null) {
    const getLen = () =>
      editor
        .getEditorState()
        .read(() => $convertToMarkdownString(DISCORD_MARKDOWN_TRANSFORMERS).length);
    const blockEvent = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      onAtLimit?.();
    };

    // Paste: check plain-text payload length against remaining capacity.
    container.addEventListener(
      "paste",
      (e) => {
        const pastedText = e.clipboardData?.getData("text/plain") || "";
        if (getLen() + pastedText.length > maxLength) blockEvent(e);
      },
      { capture: true },
    );

    // Keyboard / IME / drop insertions: block when already at the limit.
    container.addEventListener(
      "beforeinput",
      (e) => {
        if (!e.inputType.startsWith("insert") || e.inputType === "insertFromPaste") return;
        if (getLen() >= maxLength) blockEvent(e);
      },
      { capture: true },
    );
  }

  editor.setRootElement(container);
  registerRichText(editor);
  registerHistory(editor, createEmptyHistoryState(), 300);
  registerMarkdownShortcuts(editor, DISCORD_MARKDOWN_TRANSFORMERS);

  // FORMAT_TEXT_COMMAND: pre-check overhead so applying bold/italic etc. can't
  // sneak past the limit. Toggling a format OFF always allowed (reduces length).
  if (maxLength !== null) {
    editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      (format) => {
        const isAdding = editor.getEditorState().read(() => {
          const sel = $getSelection();
          return $isRangeSelection(sel) && !sel.hasFormat(format);
        });
        if (!isAdding) return false;
        const overhead = FORMAT_MD_OVERHEAD[format] ?? 4;
        const currentLen = editor
          .getEditorState()
          .read(() => $convertToMarkdownString(DISCORD_MARKDOWN_TRANSFORMERS).length);
        if (currentLen + overhead > maxLength) {
          onAtLimit?.();
          return true; // prevent Lexical from applying the format
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }

  // Paste and drag-drop bypass the trigger-based shortcut system, so we need
  // an explicit node transform to catch any variable/spoiler patterns that land
  // as plain TextNodes (e.g. pasting "{{ platform }}" all at once).
  editor.registerNodeTransform(TextNode, (node) => {
    if (!node.isSimpleText()) return;
    const text = node.getTextContent();

    const varMatch = /\{\{\s*([^}]+?)\s*\}\}/.exec(text);
    if (varMatch) {
      const s = varMatch.index;
      const l = varMatch[0].length;
      let target = node;
      if (s > 0) target = node.splitText(s)[1];
      if (s + l < text.length) target = target.splitText(l)[0];
      target.replace($createVariableNode(varMatch[0]));
      return;
    }

    const spoilerMatch = /\|\|([^|]+)\|\|/.exec(text);
    if (spoilerMatch) {
      const s = spoilerMatch.index;
      const l = spoilerMatch[0].length;
      let target = node;
      if (s > 0) target = node.splitText(s)[1];
      if (s + l < text.length) target = target.splitText(l)[0];
      target.replace($createSpoilerNode(spoilerMatch[1]));
      return;
    }
  });

  mountFloatingToolbar(container, editor, { maxLength, onAtLimit, strings });

  editor.update(() => {
    $convertFromMarkdownString(initialMarkdown || "", DISCORD_MARKDOWN_TRANSFORMERS);
  });

  // Safety-net for edge cases not covered above (drag-drop rich content, etc.).
  // The _undoPending guard prevents stacking multiple undos when spamming.
  editor.registerUpdateListener(({ editorState }) => {
    const md = editorState.read(() => $convertToMarkdownString(DISCORD_MARKDOWN_TRANSFORMERS));
    if (maxLength !== null && md.length > maxLength) {
      if (!_undoPending) {
        _undoPending = true;
        editor.dispatchCommand(UNDO_COMMAND, undefined);
        onAtLimit?.();
      }
      return;
    }
    _undoPending = false;
    onChange(md);
  });

  return editor;
}
